import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { CERT } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Award, Download, Plus, ExternalLink, Trash2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CertificatesPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "operatore";
  const [list, setList] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ worker_id: "", template_id: "", course_title: "", course_hours: "", course_topics: "", course_date: "", course_date_end: "", trainer_name: "", tutor_name: "", training_body: "Sicurgarda", trainer_signature_id: "", tutor_signature_id: "", training_body_signature_id: "", issue_date: "", expiry_date: "", normative_text: "" });

  const docenteSignatures = useMemo(() => signatures.filter((s) => s.kind === "docente"), [signatures]);
  const tutorSignatures = useMemo(() => signatures.filter((s) => s.kind === "tutor"), [signatures]);
  const bodySignatures = useMemo(() => signatures.filter((s) => s.kind === "soggetto_formatore"), [signatures]);

  const load = () => api.get("/certificates").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/workers").then((r) => setWorkers(r.data));
    api.get("/certificate-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/signatures").then((r) => setSignatures(r.data)).catch(() => {});
  }, []);

  const remove = async (id) => {
    if (!window.confirm(lang === "it" ? "Eliminare l'attestato?" : "Delete this certificate?")) return;
    try {
      await api.delete(`/certificates/${id}`);
      toast.success(lang === "it" ? "Eliminato" : "Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form,
        template_id: form.template_id || null,
        trainer_signature_id: form.trainer_signature_id || null,
        tutor_signature_id: form.tutor_signature_id || null,
        training_body_signature_id: form.training_body_signature_id || null,
        course_hours: form.course_hours ? Number(form.course_hours) : null,
        course_date: form.course_date || null,
        course_date_end: form.course_date_end || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
      };
      await api.post("/certificates/generate", payload);
      setOpen(false);
      toast.success(t("save"));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid={CERT.root} className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("certificates")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{list.length}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            {canManage ? (
              <Button data-testid={CERT.generateBtn} className="rounded-full btn-lift">
                <Plus className="w-4 h-4 mr-1.5" /> {t("generate_certificate")}
              </Button>
            ) : <span />}
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{t("generate_certificate")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} data-testid={CERT.form} className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="col-span-2">
                <Label>{t("worker")}</Label>
                <Select value={form.worker_id} onValueChange={(v) => setForm({ ...form, worker_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.last_name} {w.first_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{t("templates")}</Label>
                <Select value={form.template_id || "none"} onValueChange={(v) => setForm({ ...form, template_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cert-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Default</SelectItem>
                    {templates.map((tpl) => <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}{tpl.is_default ? " ★" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>{t("course_title")}</Label><Input required value={form.course_title} onChange={(e) => setForm({ ...form, course_title: e.target.value })} data-testid={CERT.courseTitle} /></div>
              <div><Label>{t("course_hours")}</Label><Input type="number" value={form.course_hours} onChange={(e) => setForm({ ...form, course_hours: e.target.value })} /></div>
              <div><Label>{t("training_body")}</Label><Input value={form.training_body} onChange={(e) => setForm({ ...form, training_body: e.target.value })} /></div>
              <div><Label>{t("course_date_start")}</Label><Input type="date" value={form.course_date} onChange={(e) => setForm({ ...form, course_date: e.target.value })} data-testid="cert-course-date" /></div>
              <div><Label>{t("course_date_end")} <span className="text-muted-foreground text-[10px]">({t("optional")})</span></Label><Input type="date" value={form.course_date_end} onChange={(e) => setForm({ ...form, course_date_end: e.target.value })} /></div>
              <div><Label>{t("trainer")}</Label><Input value={form.trainer_name} onChange={(e) => setForm({ ...form, trainer_name: e.target.value })} /></div>
              <div><Label>{t("tutor")}</Label><Input value={form.tutor_name} onChange={(e) => setForm({ ...form, tutor_name: e.target.value })} /></div>
              <div>
                <Label>{lang === "it" ? "Firma docente" : "Trainer signature"}</Label>
                <Select value={form.trainer_signature_id || "none"} onValueChange={(v) => setForm({ ...form, trainer_signature_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cert-trainer-sig"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {docenteSignatures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === "it" ? "Firma tutor" : "Tutor signature"}</Label>
                <Select value={form.tutor_signature_id || "none"} onValueChange={(v) => setForm({ ...form, tutor_signature_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cert-tutor-sig"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {tutorSignatures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>{lang === "it" ? "Firma soggetto formatore" : "Training body signature"}</Label>
                <Select value={form.training_body_signature_id || "none"} onValueChange={(v) => setForm({ ...form, training_body_signature_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="cert-body-sig"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {bodySignatures.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t("issue_date")}</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></div>
              <div><Label>{t("expiry_date")}</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></div>
              <div className="col-span-2"><Label>{t("course_topics")}</Label><Textarea rows={3} value={form.course_topics} onChange={(e) => setForm({ ...form, course_topics: e.target.value })} /></div>
              <div className="col-span-2"><Label>Testo normativo</Label><Textarea rows={2} value={form.normative_text} onChange={(e) => setForm({ ...form, normative_text: e.target.value })} /></div>
              <DialogFooter className="col-span-2"><Button type="submit" data-testid={CERT.submit} className="rounded-full btn-lift">{t("save")}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => {
          const worker = workers.find((w) => w.id === c.worker_id);
          return (
            <div key={c.id} className="bg-card border border-border rounded-lg p-5 card-hover">
              <div className="flex items-start justify-between gap-3">
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center"><Award className="w-5 h-5" /></div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.number}</div>
              </div>
              <div className="mt-4 font-semibold">{c.course_title}</div>
              <div className="text-xs text-muted-foreground mt-1">{worker ? `${worker.last_name} ${worker.first_name}` : c.worker_id}</div>
              <div className="text-xs text-muted-foreground">{t("issue_date")}: {c.issue_date}</div>
              <a href={`${BACKEND}${c.pdf_url}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-3 text-xs uppercase tracking-widest text-primary btn-lift">
                <span className="inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> PDF</span>
              </a>
              <a href={`/verify/${c.number}`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary btn-lift" data-testid={`verify-link-${c.number}`}>
                <ExternalLink className="w-3 h-3" /> {c.number} · verify
              </a>
              {canManage && (
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`delete-cert-${c.number}`}
                  className="mt-3 inline-flex items-center gap-1 text-[11px] text-destructive hover:text-destructive/80 btn-lift self-start"
                >
                  <Trash2 className="w-3 h-3" /> {t("delete")}
                </button>
              )}
            </div>
          );
        })}
        {list.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
      </div>
    </div>
  );
}
